import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import { CATEGORIES, ALL_FILTER, labelFor, normalizeLegacy } from "@/lib/projectCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* WorkList — public case study grid.
 *
 * Filtering rules (2026-02 iter75):
 *  • Filter chips are driven exclusively by `/app/frontend/src/lib/projectCategories.js`
 *    so backoffice + frontend can never drift again.
 *  • The "all" chip is a frontend pseudo-category; project rows never store it.
 *  • A project is included in a non-All chip only if its STORED `category`
 *    matches the chip's canonical id. We call `normalizeLegacy` on the raw
 *    value as a safety net for any pre-migration legacy row that slipped
 *    through — but we never infer category from title or services_provided.
 */
export default function WorkList() {
    const { lang } = useT();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(ALL_FILTER.id);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Cache-bust so freshly re-categorised projects reflect
                // immediately after the Studio save (no 5-minute CDN wait).
                const { data } = await axios.get(`${API}/projects?v=${Date.now()}`);
                if (alive) setProjects(data || []);
            } catch (e) {
                if (alive) setProjects([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Resolve each project to a canonical category once so (a) filter checks
    // are O(1) and (b) the card badge reads from the same source as the
    // filter — never from the raw string.
    const resolved = useMemo(() => projects.map((p) => {
        const n = normalizeLegacy(p.category);
        return { ...p, _canonicalCategory: n.id, _legacyCategory: n.id ? null : p.category };
    }), [projects]);

    const filtered = useMemo(
        () => (filter === ALL_FILTER.id
            ? resolved
            : resolved.filter((p) => p._canonicalCategory === filter)),
        [resolved, filter]
    );

    const filters = [ALL_FILTER, ...CATEGORIES];

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="work-list-page">
            <SiteNav />
            <main className="pt-28 md:pt-36 pb-24">
                <section className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-20">
                        <div>
                            <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                                {lang === "cn" ? "精选作品" : "Selected Work"}
                            </p>
                            <h1 className={cn("text-cafe-espresso leading-[0.92] text-balance text-5xl md:text-7xl lg:text-[96px]", lang === "cn" ? "font-serif-cn" : "font-serif")} style={{ fontWeight: 500 }}>
                                {lang === "cn" ? (
                                    <>静谧 <em className="font-display italic text-cafe-olive">片刻</em>,<br />深刻记忆。</>
                                ) : (
                                    <>Quiet <em className="font-display italic text-cafe-olive">moments</em>,<br />loud memories.</>
                                )}
                            </h1>
                        </div>
                        <a
                            href="https://www.instagram.com/kaily_gan/"
                            target="_blank"
                            rel="noreferrer"
                            className="group inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.26em] text-cafe-espresso/80 hover:text-cafe-olive border-b border-cafe-espresso/25 hover:border-cafe-olive pb-1 whitespace-nowrap transition-colors mb-2"
                        >
                            @kaily_gan
                            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </a>
                    </div>

                    {/* Filter bar.
                        Mobile: horizontally scrolls to show all chips with full labels — no
                        truncation, no clipping of selected state. Taps don't trigger an
                        accidental horizontal pan because each chip is `shrink-0`.
                        Desktop: wraps naturally. */}
                    <div
                        className="-mx-6 md:mx-0 px-6 md:px-0 overflow-x-auto md:overflow-visible scrollbar-none mb-12 md:mb-14"
                        data-testid="work-filters"
                    >
                        <div className="flex md:flex-wrap gap-2 whitespace-nowrap pb-2 md:pb-0">
                            {filters.map((c) => {
                                const active = filter === c.id;
                                return (
                                    <button
                                        key={c.id}
                                        data-testid={`filter-${c.id}`}
                                        onClick={() => setFilter(c.id)}
                                        className={cn(
                                            "shrink-0 h-9 px-4 rounded-full border text-[12.5px] font-medium transition-all",
                                            active
                                                ? "bg-cafe-espresso border-cafe-espresso text-cafe-cream"
                                                : "border-cafe-espresso/20 text-cafe-espresso/70 hover:border-cafe-espresso hover:text-cafe-espresso"
                                        )}
                                    >
                                        {c[lang === "cn" ? "cn" : "en"]}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-20 text-center text-cafe-mocha">
                            {lang === "cn" ? "作品加载中…" : "Loading case studies…"}
                        </div>
                    ) : filtered.length === 0 ? (
                        <EmptyState lang={lang} filterLabel={labelFor(filter, lang)} isAll={filter === ALL_FILTER.id} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
                            {filtered.map((p, i) => (
                                <ProjectCard key={p.id || p.slug} p={p} big={i % 5 === 0} lang={lang} />
                            ))}
                        </div>
                    )}
                </section>
            </main>
            <SiteFooter />
            <WhatsAppFab />
        </div>
    );
}

function ProjectCard({ p, big, lang }) {
    // Badge uses the canonical label when migrated; falls back to the raw
    // legacy value otherwise so Studio audits can spot the drift.
    const badge = p._canonicalCategory
        ? labelFor(p._canonicalCategory, lang)
        : (p._legacyCategory || "");
    return (
        <Link
            to={`/work/${p.slug}`}
            className={cn("group block relative", big && "md:col-span-2 lg:col-span-2")}
            data-testid={`project-card-${p.slug}`}
        >
            <div className={cn("overflow-hidden bg-cafe-olive", big ? "aspect-[16/9]" : "aspect-[4/5]")}>
                {p.hero_image ? (
                    <img
                        src={p.hero_image}
                        alt={p.title}
                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <span className="text-cafe-cream/70 font-display italic text-sm">Original project still</span>
                    </div>
                )}
                {p.in_progress && (
                    <span className="absolute top-3 right-3 inline-flex items-center px-2.5 py-0.5 rounded-full bg-cafe-cream/90 text-cafe-espresso text-[10px] uppercase tracking-[0.18em]">
                        {lang === "cn" ? "进行中" : "In progress"}
                    </span>
                )}
            </div>
            <div className="pt-5">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] uppercase tracking-[0.26em] font-medium text-cafe-espresso/70" data-testid={`project-badge-${p.slug}`}>
                        {badge}
                        {p.client_name && <span className="text-cafe-espresso/40 ml-2">· {p.client_name}</span>}
                    </p>
                    <ArrowUpRight className="h-3.5 w-3.5 text-cafe-espresso/40 group-hover:text-cafe-olive transition-colors" strokeWidth={1.5} />
                </div>
                <h2 className={cn("mt-2 text-2xl md:text-3xl text-cafe-espresso leading-tight", lang === "cn" ? "font-serif-cn" : "font-serif")} style={{ fontWeight: 500 }}>
                    {p.title}
                </h2>
                {p.concept && (
                    <p className="mt-1.5 font-display italic text-cafe-olive text-sm leading-snug line-clamp-1">{p.concept}</p>
                )}
            </div>
        </Link>
    );
}

function EmptyState({ lang, filterLabel, isAll }) {
    return (
        <div className="py-20 text-center border-t border-cafe-espresso/15">
            <div className="font-serif text-3xl md:text-4xl text-cafe-espresso" style={{ fontWeight: 500 }}>
                {isAll
                    ? (lang === "cn" ? "作品正在准备中" : "Case studies are being prepared")
                    : (lang === "cn" ? `暂无 ${filterLabel} 作品` : `No work under ${filterLabel} yet`)}
            </div>
            <p className="mt-4 text-cafe-espresso/60 max-w-md mx-auto">
                {lang === "cn"
                    ? (<>更多 Instagram 更新请见 <a href="https://www.instagram.com/kaily_gan/" target="_blank" rel="noreferrer" className="text-cafe-olive underline underline-offset-4">@kaily_gan</a>。</>)
                    : (<>Client-approved case studies are on the way. Meanwhile, see recent shoots on <a href="https://www.instagram.com/kaily_gan/" target="_blank" rel="noreferrer" className="text-cafe-olive underline underline-offset-4">Instagram @kaily_gan</a>.</>)}
            </p>
        </div>
    );
}
